import { useState, useEffect } from 'react'
import { fetchWithRetry } from '../utils/apiRetry'
import { useShouldLoadData } from './useInitialDataLoader'

/**
 * Hook to check backend health status and wait for it to be ready
 */
export function useHealthCheck() {
  const shouldLoad = useShouldLoadData('auth'); // Health check should happen early
  const [isHealthy, setIsHealthy] = useState(false)
  const [isChecking, setIsChecking] = useState(true)
  const [healthError, setHealthError] = useState(null)
  const [retryCount, setRetryCount] = useState(0)
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false)

  const checkHealth = async () => {
    setIsChecking(true)
    setHealthError(null)
    
    try {
      const response = await fetchWithRetry(
        '/api/health/',
        {},
        {
          maxRetries: 5,
          initialDelay: 1000,
          maxDelay: 5000,
          onRetry: (attempt) => {
            console.log(`Checking backend health... (attempt ${attempt})`)
            setRetryCount(attempt)
          }
        }
      )
      
      if (response.ok) {
        // Try to parse JSON, but handle if it's not JSON (like an HTML error page)
        try {
          const data = await response.json()
          if (data.status === 'healthy') {
            setIsHealthy(true)
          } else {
            throw new Error('Backend reported unhealthy status')
          }
        } catch (jsonError) {
          // If we can't parse JSON but got 200, something is wrong
          console.error('Health check returned non-JSON response:', jsonError)
          throw new Error('Invalid health check response')
        }
      } else {
        // Check for maintenance mode (502 is bad gateway/backend down, 521 is Cloudflare origin down, 503 is service unavailable)
        if (response.status === 502 || response.status === 521 || response.status === 503 || response.status === 504) {
          console.log(`Maintenance mode detected: HTTP ${response.status}`)
          setIsMaintenanceMode(true)
          throw new Error('maintenance')
        }
        throw new Error(`Health check failed with status ${response.status}`)
      }
    } catch (error) {
      console.error('Health check failed:', error)
      // Check if it's a maintenance error or connection failure
      const errorMessage = error.message?.toLowerCase() || ''
      
      // Detect maintenance mode or server unavailable conditions
      if (
        error.message === 'maintenance' || 
        errorMessage.includes('502') || // Bad Gateway - backend is down
        errorMessage.includes('504') || // Gateway Timeout
        errorMessage.includes('521') || // Web Server Is Down
        errorMessage.includes('522') || // Connection Timed Out
        errorMessage.includes('523') || // Origin Is Unreachable
        errorMessage.includes('524') || // A Timeout Occurred
        errorMessage.includes('503') || // Service Unavailable
        errorMessage.includes('bad gateway') || // Text from error pages
        errorMessage.includes('gateway') ||
        errorMessage.includes('cloudflare') || // Cloudflare error pages
        errorMessage.includes('failed to fetch') || // Chrome/Edge network error
        errorMessage.includes('network') || // Generic network error
        errorMessage.includes('connection') || // Connection errors
        errorMessage.includes('econnrefused') || // Node connection refused
        error.name === 'TypeError' // Fetch network errors are TypeErrors
      ) {
        console.log('Triggering maintenance mode due to error:', error.message)
        setIsMaintenanceMode(true)
        setHealthError('maintenance')
      } else {
        console.log('Not triggering maintenance mode for error:', error.message)
        setHealthError(error.message)
      }
      setIsHealthy(false)
    } finally {
      setIsChecking(false)
      setRetryCount(0)
    }
  }

  useEffect(() => {
    // Don't check until we're ready
    if (!shouldLoad) {
      setIsChecking(false); // Don't show loading spinner yet
      return;
    }
    
    // Initial health check
    checkHealth()
    
    // Recheck every 30 seconds if unhealthy
    const interval = setInterval(() => {
      if (!isHealthy && !isChecking) {
        checkHealth()
      }
    }, 30000)
    
    return () => clearInterval(interval)
  }, [shouldLoad])

  return {
    isHealthy,
    isChecking,
    healthError,
    retryCount,
    checkHealth,
    isMaintenanceMode
  }
}